import { useState, useCallback, useRef, useEffect } from 'react';
import AuthService from '../services/authService';
import { API_BASE } from '../config/api';

/**
 * useGeolocateCity — one-tap "use my current location" → a nameable home city.
 *
 * Wraps browser geolocation + the local reverse-geocode endpoint
 * (GET /api/geocode/reverse, city_centroids — no Google cost) and returns the
 * nearest city as `{ name: "Irvine, United States", coordinates: {lat, lng} }`
 * where the coordinates are the CITY's centroid, not the raw GPS fix — the
 * shape `useHomeLocation().setHome` expects, deliberately city-precision so a
 * user's exact position is never stored as their home.
 *
 * Used by the /welcome location step (prefills the autocomplete for the user
 * to confirm) and the feed's LocationNudgeCard (saves directly).
 *
 * `locate()` resolves the city object or `null` on any failure, with the
 * failure kind left in `error` for i18n-keyed messaging:
 *   'denied'      — user refused the permission prompt
 *   'unavailable' — no geolocation API / no fix within the timeout
 *   'unresolved'  — got a fix but no city within 100km (mid-ocean, etc.)
 *   'failed'      — network/server error on the reverse lookup
 */
export default function useGeolocateCity() {
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState(null);
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  const locate = useCallback(async () => {
    setError(null);
    if (!navigator.geolocation) {
      setError('unavailable');
      return null;
    }
    setLocating(true);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          // Low accuracy + 5-min cache: city-level answer, minimal battery.
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000
        });
      }).catch((err) => {
        // PERMISSION_DENIED = 1; everything else (position unavailable,
        // timeout) reads as "couldn't get a fix".
        throw new Error(err && err.code === 1 ? 'denied' : 'unavailable');
      });

      const { latitude, longitude } = position.coords;
      const res = await fetch(
        `${API_BASE}/api/geocode/reverse?lat=${latitude}&lng=${longitude}`,
        { headers: AuthService.getJsonHeaders() }
      );
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      if (!data.resolved) throw new Error('unresolved');

      const name = data.country_name ? `${data.city}, ${data.country_name}` : data.city;
      return {
        name,
        city: data.city,
        countryCode: data.country_code || null,
        coordinates: { lat: data.lat, lng: data.lng }
      };
    } catch (err) {
      const kind = ['denied', 'unavailable', 'unresolved', 'failed'].includes(err?.message)
        ? err.message : 'failed';
      if (aliveRef.current) setError(kind);
      return null;
    } finally {
      if (aliveRef.current) setLocating(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return { locate, locating, error, clearError };
}
